import { useEffect, useRef, useState } from "react";
import { Icon } from "@iconify/react";
import { enqueueSnackbar } from "notistack";
import CommonFreeTextInput from "../../common/inputs/CommonFreeTextInput";
import CommonInputContainer from "../../common/inputs/CommonInputContainer";

function OfficeIdFilePathItem({ path }) {
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(path);
      enqueueSnackbar("Copied to clipboard", { variant: "success" });
    } catch {
      enqueueSnackbar("Failed to copy", { variant: "error" });
    }
  };

  return (
    <li className="flex items-center gap-2 min-w-0">
      <span className="truncate flex-1">{path}</span>
      <button
        type="button"
        aria-label={`Copy ${path}`}
        className="shrink-0 text-[#6D7C94] hover:text-primary p-1"
        onClick={handleCopy}
      >
        <Icon icon="ion:copy-outline" className="text-lg" />
      </button>
    </li>
  );
}

const FILE_LIST_PREVIEW_COUNT = 2;

function OfficeIdFilePathList({ existingPaths = [], pendingFiles = [] }) {
  const [expanded, setExpanded] = useState(false);

  const items = [
    ...existingPaths.map((path) => ({ key: `saved-${path}`, path })),
    ...pendingFiles.map((file, i) => ({
      key: `pending-${file.name}-${i}`,
      path: file.name,
    })),
  ];

  useEffect(() => {
    setExpanded(false);
  }, [items.map((item) => item.key).join("|")]);

  if (!items.length) return null;

  const hasMore = items.length > FILE_LIST_PREVIEW_COUNT;
  const visibleItems = expanded
    ? items
    : items.slice(0, FILE_LIST_PREVIEW_COUNT);
  const hiddenCount = items.length - FILE_LIST_PREVIEW_COUNT;

  return (
    <div className="flex flex-col gap-1">
      <ul className="flex flex-col gap-1 text-sm text-[#6D7C94]">
        {visibleItems.map((item) => (
          <OfficeIdFilePathItem key={item.key} path={item.path} />
        ))}
      </ul>
      {hasMore ? (
        <button
          type="button"
          className="self-start text-xs text-primary font-medium hover:underline"
          onClick={() => setExpanded((open) => !open)}
        >
          {expanded ? "Show less" : `See ${hiddenCount} more`}
        </button>
      ) : null}
    </div>
  );
}

function useSupplierForm(supplierId, value, onChange, toDisplay, toPayload) {
  const [form, setForm] = useState({});

  useEffect(() => {
    setForm(toDisplay(value));
  }, [supplierId, value]);

  const handleChange = (name, val) => {
    const next = { ...form, [name]: val };
    setForm(next);
    onChange?.(toPayload(next));
  };

  return { form, handleChange };
}

export function toApiPayload(supplierId, form) {
  const { isActive, ...rest } = form || {};
  if (supplierId === "amadeusSoap") {
    const {
      _files,
      wsdlFileName,
      filePaths: existingPaths,
      ...fields
    } = rest || {};
    let wsdl = wsdlFileName ?? "";
    if (!wsdl && Array.isArray(existingPaths) && existingPaths[0]) {
      const path = existingPaths[0];
      if (typeof path === "string" && path.endsWith(".wsdl")) {
        wsdl = path;
      }
    }
    if (typeof wsdl === "string" && wsdl.endsWith(".wsdl")) {
      wsdl = wsdl.slice(0, -5);
    }
    return {
      ...fields,
      ...(wsdl ? { wsdlFileName: wsdl } : {}),
      _files: _files ?? [],
    };
  }
  return rest;
}

export function OfficeIdAmadeusForm({ value, onChange }) {
  const { form, handleChange } = useSupplierForm(
    "amadeus",
    value,
    onChange,
    (v) => ({
      clientId: v?.clientId ?? "",
      clientSecret: v?.clientSecret ?? "",
    }),
    (f) => toApiPayload("amadeus", f),
  );

  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-[#6D7C94]">
        Enter Amadeus REST API credentials for this agent.
      </p>
      <CommonFreeTextInput
        label="Client Id"
        placeholder="ex. 7d82cbe28obcehdw8c76eycedc"
        value={form.clientId}
        onChange={(val) => handleChange("clientId", val)}
      />
      <CommonFreeTextInput
        label="Client Secret"
        placeholder="ex. 39ee3heyudgw"
        type="password"
        value={form.clientSecret}
        onChange={(val) => handleChange("clientSecret", val)}
      />
    </div>
  );
}

export function OfficeIdTravelportForm({ value, onChange }) {
  const { form, handleChange } = useSupplierForm(
    "travelport",
    value,
    onChange,
    (v) => ({
      username: v?.username ?? "",
      password: v?.password ?? "",
      targetBranch: v?.targetBranch ?? "",
    }),
    (f) => toApiPayload("travelport", f),
  );

  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-[#6D7C94]">
        Enter Travelport Universal API credentials and target branch.
      </p>
      <CommonFreeTextInput
        label="Username"
        placeholder="ex. Universal API/6372dgt72dt32732c322"
        value={form.username}
        onChange={(val) => handleChange("username", val)}
      />
      <CommonFreeTextInput
        label="Password"
        placeholder="ex. &gybe2ccs"
        type="password"
        value={form.password}
        onChange={(val) => handleChange("password", val)}
      />
      <CommonFreeTextInput
        label="Target Branch"
        placeholder="ex. UB489DS"
        value={form.targetBranch}
        onChange={(val) => handleChange("targetBranch", val)}
      />
    </div>
  );
}

export function OfficeIdSabreForm({ value, onChange }) {
  const { form, handleChange } = useSupplierForm(
    "sabre",
    value,
    onChange,
    (v) => ({
      userId: v?.userId ?? "",
      password: v?.password ?? "",
      clientId: v?.clientId ?? "",
      clientSecret: v?.clientSecret ?? "",
    }),
    (f) => toApiPayload("sabre", f),
  );

  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-[#6D7C94]">
        Enter Sabre credentials for this agent.
      </p>
      <CommonFreeTextInput
        label="User ID"
        placeholder="ex. V1:userid:group:domain"
        value={form.userId}
        onChange={(val) => handleChange("userId", val)}
      />
      <CommonFreeTextInput
        label="Password"
        placeholder="Password"
        type="password"
        value={form.password}
        onChange={(val) => handleChange("password", val)}
      />
      <CommonFreeTextInput
        label="Client Id"
        placeholder="ex. client id"
        value={form.clientId}
        onChange={(val) => handleChange("clientId", val)}
      />
      <CommonFreeTextInput
        label="Client Secret"
        placeholder="ex. client secret"
        type="password"
        value={form.clientSecret}
        onChange={(val) => handleChange("clientSecret", val)}
      />
    </div>
  );
}

function WsdlEntryInput({ value, onChange }) {
  return (
    <CommonInputContainer label="WSDL entry filename">
      <div className="relative flex items-center">
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="WSDL entry filename"
          className="w-full h-12 items-center px-4 pr-14 border border-[#DBDBDB] rounded-lg placeholder:text-[#A4B1C1] focus:outline-none"
        />
        <span className="absolute right-4 text-[#A4B1C1] pointer-events-none">
          .wsdl
        </span>
      </div>
    </CommonInputContainer>
  );
}

export function OfficeIdAmadeusSoapForm({
  value,
  onChange,
  onWsdlFilesChange,
}) {
  const fileInputRef = useRef(null);
  const formRef = useRef(null);
  const [form, setForm] = useState({
    username: "",
    password: "",
    officeId: "",
    dutyCode: "",
    wsap: "",
    wsdlFileName: "",
  });
  const [pendingFiles, setPendingFiles] = useState([]);

  formRef.current = form;

  useEffect(() => {
    const v = value || {};
    let wsdl = v.wsdlFileName ?? v.wsdl ?? "";
    if (typeof wsdl === "string" && wsdl.endsWith(".wsdl")) {
      wsdl = wsdl.slice(0, -5);
    }
    const nextForm = {
      username: v.username ?? "",
      password: v.password ?? "",
      officeId: v.officeId ?? "",
      dutyCode: v.dutyCode ?? "",
      wsap: v.wsap ?? "",
      wsdlFileName: wsdl,
    };
    setForm(nextForm);
    formRef.current = nextForm;
    setPendingFiles([]);
    onWsdlFilesChange?.([]);
    onChange?.(
      toApiPayload("amadeusSoap", {
        ...nextForm,
        filePaths: v?.filePaths,
        _files: [],
      }),
    );
  }, [value]);

  const emitChange = (nextForm, nextFiles = pendingFiles) => {
    onChange?.(
      toApiPayload("amadeusSoap", {
        ...nextForm,
        filePaths: value?.filePaths,
        _files: nextFiles,
      }),
    );
  };

  const handleFieldChange = (name, val) => {
    const next = { ...form, [name]: val };
    setForm(next);
    emitChange(next);
  };

  const handleFilesAdded = (fileList) => {
    const added = Array.from(fileList || []).filter((file) =>
      /\.zip$/i.test(file?.name || ""),
    );
    if (!added.length) {
      enqueueSnackbar("Please upload a ZIP file containing the WSDL", {
        variant: "error",
      });
      return;
    }
    const nextFiles = added.slice(0, 1);
    setPendingFiles(nextFiles);
    onWsdlFilesChange?.(nextFiles);
    emitChange(formRef.current, nextFiles);
  };

  const existingPaths = Array.isArray(value?.filePaths) ? value.filePaths : [];
  const selectedZip = pendingFiles[0];

  const handleClearSelectedZip = (e) => {
    e.stopPropagation();
    setPendingFiles([]);
    onWsdlFilesChange?.([]);
    emitChange(formRef.current, []);
  };

  return (
    <div className="flex flex-col gap-4">
      <CommonFreeTextInput
        label="Username"
        placeholder="Username"
        value={form.username}
        onChange={(val) => handleFieldChange("username", val)}
      />
      <CommonFreeTextInput
        label="Password"
        placeholder="Password"
        type="password"
        value={form.password}
        onChange={(val) => handleFieldChange("password", val)}
      />
      <CommonFreeTextInput
        label="Office ID"
        placeholder="Office ID"
        value={form.officeId}
        onChange={(val) => handleFieldChange("officeId", val)}
      />
      <CommonFreeTextInput
        label="Duty Code"
        placeholder="Duty Code"
        value={form.dutyCode}
        onChange={(val) => handleFieldChange("dutyCode", val)}
      />
      <CommonFreeTextInput
        label="WSAP"
        placeholder="WSAP"
        value={form.wsap}
        onChange={(val) => handleFieldChange("wsap", val)}
      />
      <WsdlEntryInput
        value={form.wsdlFileName}
        onChange={(val) => handleFieldChange("wsdlFileName", val)}
      />

      <p className="text-xs text-[#6D7C94]">
        Upload a ZIP archive containing your WSDL file. The entry filename above
        must match the WSDL file inside the ZIP.
      </p>

      <CommonInputContainer label="WSDL ZIP file">
        <div
          role="button"
          tabIndex={0}
          className={`flex w-full items-center gap-2 min-h-12 px-4 py-2 border rounded-lg cursor-pointer transition-colors ${
            selectedZip
              ? "border-primary bg-[#E8F0FE] hover:bg-[#DCE8FD]"
              : "border-[#DBDBDB] text-[#6D7C94] hover:bg-[#F9FAFB]"
          }`}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault();
            handleFilesAdded(e.dataTransfer.files);
          }}
          onClick={() => fileInputRef.current?.click()}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              fileInputRef.current?.click();
            }
          }}
        >
          {selectedZip ? (
            <>
              <Icon
                icon="material-symbols:check-circle"
                className="text-xl text-primary shrink-0"
              />
              <div className="flex flex-1 flex-col min-w-0 text-left">
                <span className="text-sm font-medium text-primary truncate">
                  {selectedZip.name}
                </span>
                <span className="text-xs text-[#6D7C94]">
                  ZIP file selected · Click to replace
                </span>
              </div>
              <button
                type="button"
                aria-label="Remove selected ZIP file"
                className="shrink-0 text-[#6D7C94] hover:text-red-500 p-1"
                onClick={handleClearSelectedZip}
              >
                <Icon icon="material-symbols:close" className="text-xl" />
              </button>
            </>
          ) : (
            <>
              <Icon icon="material-symbols:upload" className="text-xl shrink-0" />
              <span className="text-sm">Choose / Drop a ZIP file here</span>
            </>
          )}
        </div>
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          accept=".zip,application/zip"
          onChange={(e) => {
            handleFilesAdded(e.target.files);
            e.target.value = "";
          }}
        />
      </CommonInputContainer>

      <OfficeIdFilePathList existingPaths={existingPaths} pendingFiles={[]} />
    </div>
  );
}

export function OfficeIdSupplierFormPanel({
  supplierId,
  value,
  onChange,
  onWsdlFilesChange,
}) {
  switch (supplierId) {
    case "amadeus":
      return <OfficeIdAmadeusForm value={value} onChange={onChange} />;
    case "travelport":
      return <OfficeIdTravelportForm value={value} onChange={onChange} />;
    case "sabre":
      return <OfficeIdSabreForm value={value} onChange={onChange} />;
    case "amadeusSoap":
      return (
        <OfficeIdAmadeusSoapForm
          value={value}
          onChange={onChange}
          onWsdlFilesChange={onWsdlFilesChange}
        />
      );
    default:
      return null;
  }
}
